'use client';

import { c as _c } from "react/compiler-runtime";
import { Heading, Text, VStack } from '@jaaq/hub-ui';
import { logger } from '@jaaq/observability';
import { type ReactElement, useEffect, useRef } from 'react';
import { OnboardingShell } from '#components/onboarding-shell';
import { useOnboardingState } from '#lib/onboarding-provider';
import { buildOnboardingResults, type SaveOnboardingResults } from '#lib/results/onboarding-results';
type ProvisioningStepProps = {
  /**
   * Persists the wizard answers. Injected by the host (via `OnboardingRouter`)
   * because the action resolves the signed-in company server-side from `hub`'s
   * auth — see `SaveOnboardingResults`.
   */
  saveResults: SaveOnboardingResults;
};

/**
 * "Setting up JAAQ" loader — Figma "77" (1272:10102).
 *
 * Spinner + short subhead "Tailoring to your needs". Auto-advances after
 * a fake delay; replace with a real backend status poll once provisioning
 * is wired up.
 */
export const ProvisioningStep = t0 => {
  const $ = _c(8);
  const {
    saveResults
  } = t0;
  const {
    goNext,
    onboardingState
  } = useOnboardingState();
  const hasSaved = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onboardingState || $[1] !== saveResults) {
    t1 = () => {
      if (hasSaved.current) {
        return;
      }
      hasSaved.current = true;
      saveResults(buildOnboardingResults(onboardingState)).then(_temp);
    };
    t2 = [onboardingState, saveResults];
    $[0] = onboardingState;
    $[1] = saveResults;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== goNext) {
    t3 = () => {
      const t = window.setTimeout(goNext, 3500);
      return () => window.clearTimeout(t);
    };
    t4 = [goNext];
    $[4] = goNext;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <OnboardingShell showProgress={false} showActions={false} centred={true}><VStack align="center" justify="center" className="mx-auto max-w-lg min-h-[55vh]"><Spinner /><Heading level={1} variant="display" tone="inverse" className="mt-8 text-4xl md:text-5xl">Setting up JAAQ</Heading><Text variant="body-lg" tone="inverse" muted={true} className="mt-3">Tailoring to your needs</Text></VStack></OnboardingShell>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const Spinner = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span role="status" aria-label="Loading" className="size-12 rounded-full border-2 border-white/20 border-t-fg-inverse animate-spin" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(result) {
  if (!result.ok) {
    logger.warn("onboarding: failed to save results", {
      error: result.error
    });
  }
}